import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, UserPlus, UserX, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const GrantRow = ({ g, onRevoke, onReviewed }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-0" data-testid={`portal-grant-${g.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium">{g.name || g.email || g.user_id}</p>
      <p className="text-xs text-muted-foreground">{g.email} · {g.source} · granted {fmt(g.granted_at)}{g.revoked_at ? ` · revoked ${fmt(g.revoked_at)}` : ''}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {g.revoked_at ? <Badge variant="outline">No access</Badge> : <Badge>Access</Badge>}
      {!g.revoked_at && !g.reviewed_at && (
        <Button size="sm" variant="outline" onClick={() => onReviewed(g.id)} data-testid={`portal-grant-review-${g.id}`}>
          <CheckCircle2 className="w-4 h-4 mr-1" />Mark reviewed
        </Button>
      )}
      {!g.revoked_at && (
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onRevoke(g.id)} data-testid={`portal-grant-revoke-${g.id}`}>
          <UserX className="w-4 h-4 mr-1" />Revoke
        </Button>
      )}
    </div>
  </div>
);

export const PortalAccessTab = ({ clientId }) => {
  const [data, setData] = useState({ grants: [], candidates: [] });
  const load = useCallback(() => api.get(`/api/portal-access/clients/${clientId}`).then(({ data: d }) => setData(d)).catch(() => toast.error('Could not load portal access')), [clientId]);
  useEffect(() => { load(); }, [load]);

  const act = (p, msg) => p.then(() => { toast.success(msg); load(); }).catch((e) => toast.error(e.response?.data?.detail || 'Action failed'));
  const onRevoke = (id) => act(api.post(`/api/portal-access/grants/${id}/revoke`, { reason: 'Revoked by staff' }), 'Access revoked – effective on the user\'s next request');
  const onReviewed = (id) => act(api.post(`/api/portal-access/grants/${id}/reviewed`), 'Marked reviewed');
  const onGrant = (uid) => act(api.post('/api/portal-access/grants', { user_id: uid, client_id: clientId }), 'Access granted');

  return (
    <Card data-testid="portal-access-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="w-4 h-4" />Portal access to this entity</CardTitle>
        <p className="text-sm text-muted-foreground">Each portal user either has access or no access. Changes apply on the user's next request across all their sessions.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          {data.grants.length === 0 && <p className="text-sm text-muted-foreground" data-testid="portal-access-empty">No portal users have access.</p>}
          {data.grants.map((g) => <GrantRow key={g.id} g={g} onRevoke={onRevoke} onReviewed={onReviewed} />)}
        </div>
        {data.candidates.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Group portal users without access</p>
            {data.candidates.map((c) => (
              <div key={c.user_id} className="flex items-center justify-between py-2" data-testid={`portal-candidate-${c.user_id}`}>
                <span className="text-sm">{c.name || c.email} <span className="text-muted-foreground">{c.email}</span></span>
                <Button size="sm" variant="outline" onClick={() => onGrant(c.user_id)} data-testid={`portal-grant-add-${c.user_id}`}>
                  <UserPlus className="w-4 h-4 mr-1" />Grant access
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
